import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { api, ApiError } from '../api/client';
import { FolderTreeDiagram, FolderTreeNode } from '../api/types';
import { getDraggedDiagramId, isDiagramDrag, notifyDiagramMoved, setDraggedDiagramId } from '../utils/dnd';
import { confirmLeaveIfPending } from '../utils/pendingSave';

function DiagramLeaf({ diagram, depth, canRename }: { diagram: FolderTreeDiagram; depth: number; canRename: boolean }) {
  const { t } = useTranslation();
  const { diagramId } = useParams();
  const navigate = useNavigate();
  const [renaming, setRenaming] = useState(false);
  const [value, setValue] = useState(diagram.name);
  const [name, setName] = useState(diagram.name);

  const commitRename = async () => {
    setRenaming(false);
    const trimmed = value.trim();
    if (!trimmed || trimmed === name) {
      setValue(name);
      return;
    }
    try {
      await api.patch(`/diagrams/${diagram.id}`, { name: trimmed });
      setName(trimmed);
      notifyDiagramMoved();
    } catch (err) {
      alert(err instanceof ApiError ? err.message : String(err));
      setValue(name);
    }
  };

  if (renaming) {
    return (
      <div style={{ paddingLeft: 8 + (depth + 1) * 14, paddingRight: 6, paddingTop: 4, paddingBottom: 4 }}>
        <input
          autoFocus
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onBlur={commitRename}
          onKeyDown={(e) => {
            if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
            if (e.key === 'Escape') {
              setValue(name);
              setRenaming(false);
            }
          }}
          style={{ width: '100%', fontSize: 13, padding: '2px 4px' }}
        />
      </div>
    );
  }

  return (
    <div
      className={`tree-node${diagramId === diagram.id ? ' selected' : ''}`}
      style={{ paddingLeft: 8 + (depth + 1) * 14 }}
      draggable={canRename}
      onDragStart={(e) => canRename && setDraggedDiagramId(e, diagram.id)}
      onClick={() => {
        if (!confirmLeaveIfPending(t('common.leaveWithPendingSave') as string)) return;
        navigate(`/diagrams/${diagram.id}`);
      }}
      onDoubleClick={() => canRename && setRenaming(true)}
      title={canRename ? (t('folders.renameHint') as string) : undefined}
    >
      <span>📊 {name}</span>
    </div>
  );
}

function Node({ node, depth }: { node: FolderTreeNode; depth: number }) {
  const { t } = useTranslation();
  const { folderId } = useParams();
  const navigate = useNavigate();
  const selected = folderId === node.id;
  const [dragOver, setDragOver] = useState(false);
  const canDrop = node.permissionLevel === 'WRITE' || node.permissionLevel === 'ADMIN';

  const handleDragOver = (e: React.DragEvent) => {
    if (!canDrop || !isDiagramDrag(e)) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = async (e: React.DragEvent) => {
    if (!canDrop) return;
    e.preventDefault();
    setDragOver(false);
    const draggedId = getDraggedDiagramId(e);
    if (!draggedId) return;
    try {
      await api.patch(`/diagrams/${draggedId}/move`, { targetFolderId: node.id });
      notifyDiagramMoved();
    } catch (err) {
      alert(err instanceof ApiError ? err.message : String(err));
    }
  };

  return (
    <div>
      <div
        className={`tree-node${selected ? ' selected' : ''}${dragOver ? ' drop-target' : ''}`}
        style={{ paddingLeft: 8 + depth * 14 }}
        onClick={() => {
          if (!confirmLeaveIfPending(t('common.leaveWithPendingSave') as string)) return;
          navigate(`/folders/${node.id}`);
        }}
        onDragOver={handleDragOver}
        onDragEnter={() => canDrop && setDragOver(true)}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
      >
        <span>📁 {node.name}</span>
      </div>
      {node.children.map((child) => (
        <Node key={child.id} node={child} depth={depth + 1} />
      ))}
      {node.diagrams.map((d) => (
        <DiagramLeaf key={d.id} diagram={d} depth={depth} canRename={canDrop} />
      ))}
    </div>
  );
}

export function FolderTree({ nodes }: { nodes: FolderTreeNode[] }) {
  const { t } = useTranslation();
  if (nodes.length === 0) {
    return <p style={{ padding: '8px', fontSize: 13, color: 'var(--color-text-muted)' }}>{t('folders.noAccess')}</p>;
  }
  return (
    <div>
      {nodes.map((node) => (
        <Node key={node.id} node={node} depth={0} />
      ))}
    </div>
  );
}
